import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import {
  batchIsPendingExport,
  isDeliveryFeeLine,
  mapDetItemCode,
  markBatchesExported,
  type DemOrderBatch,
  type ExportRow,
} from './demOrderBatches';

export type ExportableOrderItem = {
  item_code: string;
  description: string;
  qty: number;
  uom: string;
  batch_id?: string | null;
};

export type ExportableOrder = {
  order_id: string;
  customer_code: string;
  delivery_date: string;
  delivery_date_display?: string;
  ref_no: string;
  waive_delivery?: boolean;
  items: ExportableOrderItem[];
  batches?: DemOrderBatch[];
};

function formatExportDeliveryDate(order: ExportableOrder): string {
  const raw = order.delivery_date_display || order.delivery_date;
  const match = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    const [, dd, mm, yyyy] = match;
    return `${dd.padStart(2, '0')}${mm.padStart(2, '0')}${yyyy}`;
  }
  const iso = order.delivery_date.split('T')[0] ?? '';
  const [y, m, d] = iso.split('-');
  if (y && m && d) return `${d}${m}${y}`;
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
}

function formatTodayDmyCompact(): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(new Date());
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const y = parts.find((p) => p.type === 'year')?.value ?? '2026';
  return `${d}${m}${y}`;
}

function formatExportPoNumber(refNo: string): string {
  const value = refNo.replace(/,/g, ' ').trim();
  if (!value || value === '—' || value === '–' || value === '-' || /^n\/?a$/i.test(value)) {
    return '';
  }
  return value;
}

/**
 * Build HDR/DET for pending export waves.
 * - DET only from unexported batches' lines.
 * - HDR only when batch 0 is still pending (first export) or legacy orders with no batches.
 * - Add-on waves (batch 1+) are DET-only; same SeqNo so NAV attaches lines to the existing SO.
 */
export function buildBatchAwareExportRows(
  orders: ExportableOrder[],
  opts?: { skipWaivedDeliveryFee?: boolean },
): {
  hdrRows: ExportRow[];
  detRows: ExportRow[];
  orderIds: string[];
  batchIds: string[];
} {
  const hdrRows: ExportRow[] = [];
  const detRows: ExportRow[] = [];
  const orderIds: string[] = [];
  const batchIds: string[] = [];
  const importDate = formatTodayDmyCompact();
  const skipWaived = Boolean(opts?.skipWaivedDeliveryFee);

  for (const order of orders) {
    const oid = order.order_id.trim();
    if (!oid) continue;

    const batches = [...(order.batches ?? [])].sort((a, b) => a.batch_no - b.batch_no);
    const pendingBatches = batches.filter((b) => batchIsPendingExport(b));
    const useLegacy = batches.length === 0;
    const pendingBatchIds = new Set(useLegacy ? [] : pendingBatches.map((b) => b.id).filter(Boolean));

    if (!useLegacy && pendingBatches.length === 0) continue;

    const pendingItems = order.items.filter((item) => {
      if (useLegacy) return true;
      const bid = item.batch_id?.trim() ?? '';
      if (!bid) return pendingBatches.some((b) => b.batch_no === 0);
      return pendingBatchIds.has(bid);
    });

    const exportItems = pendingItems.filter((item) => {
      if (skipWaived && order.waive_delivery && isDeliveryFeeLine(item.item_code, item.description)) {
        return false;
      }
      return true;
    });

    if (exportItems.length === 0 && !useLegacy) continue;

    const includeHdr = useLegacy || pendingBatches.some((b) => b.batch_no === 0);
    if (includeHdr) {
      hdrRows.push({
        SeqNo: oid,
        CustomerCode: order.customer_code.trim(),
        ImportDate: importDate,
        DeliveryDate: formatExportDeliveryDate(order),
        Empty1: '',
        PO_Number: formatExportPoNumber(order.ref_no),
        Default: 0,
      });
    }

    for (const item of exportItems) {
      detRows.push({
        SeqNo: oid,
        ItemCode: mapDetItemCode(item.item_code, item.description),
        Quantity: item.qty,
        Default: 0,
        SystemUOM: item.uom,
      });
    }

    orderIds.push(oid);
    if (!useLegacy) {
      for (const b of pendingBatches) {
        if (b.id) batchIds.push(b.id);
      }
    }
  }

  return { hdrRows, detRows, orderIds, batchIds };
}

export async function finalizeOrderExport(input: {
  orderIds: string[];
  batchIds: string[];
  exportedBy: string;
}): Promise<void> {
  if (input.batchIds.length) {
    await markBatchesExported(input.batchIds, input.exportedBy);
  }

  const orderIds = [...new Set(input.orderIds.map((id) => id.trim()).filter(Boolean))];
  if (!orderIds.length) return;

  const db = getDbSupabase('dem');
  const { error } = await db
    .from('orders')
    .update({
      status: 'Uploading',
      exported_by: null,
    })
    .in('order_id', orderIds);
  if (error) throw new Error(formatSupabaseError(error));

  // Deduct open blanket outstanding by matching PO (ref_no) + item code.
  try {
    const { applyBlanketDeductionsForExport } = await import('./demBlanketOrders');
    await applyBlanketDeductionsForExport({
      orderIds,
      batchIds: input.batchIds,
      exportedBy: input.exportedBy,
    });
  } catch (blanketError) {
    // Export already queued — surface but do not roll back NAV upload.
    console.error('Blanket deduction failed after export:', blanketError);
  }
}
